// The large centred progress view shown while images are being converted.
//
// [Brian 2026-10-01] "we want that to be large and centered and track each files upload %"
//
// 🔴 ONLY THE UPLOAD HAS A REAL PERCENTAGE. `xhr.upload.onprogress` reports bytes sent; the server's
// own work reports nothing at all (0.4 s for JPEG, ~1.2 s for AVIF, plus a HEIC decode where that
// applies). So a file's bar fills honestly to the end of its upload and then switches to a moving
// indeterminate stripe for the convert — rather than a fake creep to 99% that stalls, which is the
// usual way progress UI lies.
import { Loader2 } from 'lucide-react';
import {
  overallFraction, filePercent, savedLabel, shortName,
  type FileProgress,
} from '@/lib/image-progress-math';

export type { FileProgress, Phase } from '@/lib/image-progress-math';

const RING = 54;                       // radius; the ring is the biggest thing on the screen
const CIRC = 2 * Math.PI * RING;


export function ImageProgress({ files, format }: { files: FileProgress[]; format: 'jpeg' | 'avif' }) {
  const overall = overallFraction(files);
  const pct = Math.round(overall * 100);
  const finished = files.filter((f) => f.phase === 'done' || f.phase === 'kept').length;

  return (
    <div className="flex flex-col items-center py-2">
      {/* The ring. aria-hidden because the live region below says the same thing in words. */}
      <div className="relative" aria-hidden="true">
        <svg width={140} height={140} className="-rotate-90">
          <circle cx={70} cy={70} r={RING} fill="none" strokeWidth={10}
                  className="stroke-bmail-border" />
          <circle cx={70} cy={70} r={RING} fill="none" strokeWidth={10} strokeLinecap="round"
                  className="stroke-bmail-goldfill transition-[stroke-dashoffset] duration-300 ease-out"
                  strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - overall)} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-semibold tabular-nums text-bmail-text">{pct}%</span>
          <span className="text-xs text-bmail-text-secondary">
            {finished} of {files.length}
          </span>
        </div>
      </div>

      <p className="mt-3 flex items-center gap-2 text-sm text-bmail-text-secondary" role="status" aria-live="polite">
        <Loader2 size={14} className="animate-spin" />
        Converting to {format === 'avif' ? 'AVIF' : 'JPEG'} — {pct}% done
      </p>

      {/* Per-file rows. This is the "track each file's upload %" half. */}
      <ul className="mt-4 w-full space-y-2">
        {files.map((f, i) => {
          const filePct = filePercent(f);
          const saved = savedLabel(f);
          return (
            <li key={`${f.filename}-${i}`} className="text-xs">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-bmail-text">{shortName(f.filename)}</span>
                <span className="shrink-0 tabular-nums text-bmail-text-secondary">
                  {f.phase === 'queued' && 'waiting'}
                  {f.phase === 'uploading' && `${filePct}%`}
                  {f.phase === 'converting' && 'converting…'}
                  {f.phase === 'done' && (saved ?? 'done')}
                  {f.phase === 'kept' && 'kept as-is'}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-bmail-border">
                {f.phase === 'converting' ? (
                  // Indeterminate: the server gives no number, so neither do we.
                  <div className="h-full w-1/3 animate-bmail-convert rounded-full bg-bmail-goldfill" />
                ) : (
                  <div
                    className="h-full rounded-full bg-bmail-goldfill transition-[width] duration-200 ease-out"
                    style={{ width: `${filePct}%` }}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
